import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUserSignal, impersonatingUser, isImpersonating } from '@adornis/users/client/currentUser.js';
import { html, nothing } from 'lit';
import { stopImpersonating } from '../../features/user-management/client/impersonation.js';

/**
 * Sits above the whole app for as long as the session runs as somebody else — without it
 * nothing on screen says that the account shown is not the one that logged in.
 *
 * Both observables are fed by subscribeCurrentUser, which every impersonation change calls
 * (see features/user-management/client/impersonation.ts), so this also comes back up by
 * itself after a reload: the impersonation lives on the session, not in the tab.
 */
export const ImpersonationBanner = stateful(() => {
  const impersonating = useObservable(() => isImpersonating, []);
  // The administrator behind the mask. Null while their document has not arrived yet.
  const administrator = useObservable(() => impersonatingUser(), []);
  const [busy, setBusy] = useState(false);

  if (!impersonating) return nothing;

  const account = currentUserSignal.value;
  const accountName = account?.email || account?.username || 'unbekannt';
  const administratorName = administrator?.email || administrator?.username;

  const stop = async () => {
    setBusy(true);
    try {
      await stopImpersonating();
      // Back to where the takeover was started from. Only after the operation returned — the
      // page checks the permission that this very call hands back.
      goTo('/users');
      XSnackbar.show('Übernahme beendet.');
    } catch (e: any) {
      XSnackbar.show(e?.message ?? 'Die Übernahme konnte nicht beendet werden.');
    } finally {
      // Hidden is not unmounted: the directive stays in its part and keeps its hooks, so a
      // flag left standing here would still be set at the next takeover — button dead until
      // the page is reloaded.
      setBusy(false);
    }
  };

  return html`
    <div class="flex items-center gap-3 px-4 py-2 bg-warning text-warning-content shrink-0">
      <x-icon class="text-lg shrink-0">switch_account</x-icon>
      <div class="text-sm min-w-0 truncate">
        <span class="font-semibold">Übernommene Sitzung</span>
        <span class="opacity-80">
          — du arbeitest gerade als ${accountName}${administratorName ? html` statt als ${administratorName}` : nothing}
        </span>
      </div>
      <button class="btn btn-sm ml-auto shrink-0" ?disabled=${busy} @click=${stop}>
        <x-icon class="text-base">undo</x-icon>
        Zurück zu meinem Konto
      </button>
    </div>
  `;
});
